/**
 * MapView Component
 *
 * OpenLayers map with ol-ext LayerSwitcher for base map selection.
 *
 * Usage:
 *   import { MapView } from './components/MapView.js';
 *
 *   const map = new MapView('map', {
 *     center: [-1.5, 7.5],  // Ghana
 *     zoom: 7,
 *     basemap: 'osm'
 *   });
 *
 *   map.onClick((lon, lat) => console.log('Clicked:', lon, lat));
 *   map.addMarker(lon, lat, { name: 'Point A' });
 */

import Map from 'ol/Map';
import View from 'ol/View';
import Overlay from 'ol/Overlay';
import TileLayer from 'ol/layer/Tile';
import LayerGroup from 'ol/layer/Group';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Cluster from 'ol/source/Cluster';
import GeoJSON from 'ol/format/GeoJSON';
import OSM from 'ol/source/OSM';
import XYZ from 'ol/source/XYZ';
import { fromLonLat, toLonLat } from 'ol/proj';
import { getDistance } from 'ol/sphere';
import { Point, LineString, Polygon as PolygonGeom } from 'ol/geom';
import Feature from 'ol/Feature';
import { Style, Circle, Fill, Stroke, Text } from 'ol/style';

// ol-ext LayerSwitcher
import LayerSwitcher from 'ol-ext/control/LayerSwitcher';

// ol-ext GeolocationButton — superseded by the custom ls-locate control
// (see _createLocationControl). Import removed; the control is built from
// plain DOM so it can host both Locate-me and Record-trail actions.

// ol-ext SearchNominatim
import SearchNominatim from 'ol-ext/control/SearchNominatim';

// CSS imports
import 'ol/ol.css';
import 'ol-ext/dist/ol-ext.css';

// i18n
import { t, getCategoryLabel, onLanguageChange } from '../i18n.js';

// Shared frosted-glass form system (BisaWofa form design principle)
import { openGlassModal } from './glassForm.js';

export class MapView {
  constructor(targetId, options = {}) {
    this.options = options;
    this.markerSource = new VectorSource();
    this.clickCallbacks = [];

    // Category emoji mapping (emojis are language-independent)
    this.categoryEmojis = {
      'default': { emoji: '📍' },
      'water': { emoji: '💧' },
      'school': { emoji: '🏫' },
      'health': { emoji: '🏥' },
      'hotel': { emoji: '🏨' },
      'restaurant': { emoji: '🍽' },
      'market': { emoji: '🏪' },
      'other': { emoji: '📌' }
    };

    // Helper to get emoji for a category
    this.getEmoji = (category) => {
      const cat = this.categoryEmojis[category];
      return cat ? cat.emoji : '📍';
    };

    // Helper to generate category options HTML for select dropdowns (i18n-aware)
    this.getCategoryOptionsHtml = () => {
      return Object.entries(this.categoryEmojis)
        .map(([key, { emoji }]) =>
          `<option value="${key}">${emoji} ${this.escapeHtml(getCategoryLabel(key))}</option>`
        )
        .join('\n            ');
    };

    // Create emoji style helper
    this.createEmojiStyle = (emoji, fontSize = 24) => {
      return new Style({
        text: new Text({
          text: emoji,
          font: `${fontSize}px sans-serif`,
          textBaseline: 'bottom',
          textAlign: 'center',
          offsetY: -5,
        }),
      });
    };

    // Default marker style (pin emoji)
    this.defaultStyle = this.createEmojiStyle('📍', 32);

    // Selected marker style (larger)
    this.selectedStyle = this.createEmojiStyle('📍', 42);

    // Initialize category styles with emojis
    this.categoryStyles = {};
    for (const [category, { emoji }] of Object.entries(this.categoryEmojis)) {
      this.categoryStyles[category] = this.createEmojiStyle(emoji, 32);
    }

    // Create base layers group
    const baseLayers = this.createBaseLayers(options.basemap || 'osm');

    // Markers layer (user-added locations)
    this.markersLayer = new VectorLayer({
      title: 'Markers',
      source: this.markerSource,
      style: (feature) => this.getFeatureStyle(feature),
    });

    // ── POI Layer (from PostGIS / OSM data) ──────────────────────────────
    this.poiSource = new VectorSource();

    // Amenity → emoji mapping for OSM POI types
    this.amenityEmojis = {
      'fuel': '⛽',
      'bus_station': '🚌',
      'taxi': '🚕',
      'pharmacy': '💊',
      'hospital': '🏥',
      'bank': '🏦',
      'atm': '🏧',
      'police': '🚔',
      'fire_station': '🚒',
      'place_of_worship': '⛪',
      'school': '🏫',
      'marketplace': '🏪',
    };

    // Cluster source wraps raw POI source for grouped rendering.
    // geometryFunction excludes filtered amenities from clustering.
    this._clusterStyleCache = {};
    this.clusterSource = new Cluster({
      source: this.poiSource,
      distance: 45,
      minDistance: 20,
      geometryFunction: (feature) => {
        const amenity = feature.get('amenity') || '';
        if (this.poiFilterSet && !this.poiFilterSet.has(amenity)) {
          return null;
        }
        return feature.getGeometry();
      },
    });

    this.poiLayer = new VectorLayer({
      title: 'POI',
      source: this.clusterSource,
      visible: true,
      style: (feature) => this.getClusterStyle(feature),
    });

    // Overlay layers group (appears in LayerSwitcher)
    this.overlayGroup = new LayerGroup({
      title: 'Overlays',
      layers: [
        this.poiLayer,
        this.markersLayer,
      ],
    });

    // Create map
    this.map = new Map({
      target: targetId,
      layers: [
        baseLayers,
        this.overlayGroup,
      ],
      view: new View({
        center: fromLonLat(options.center || [0, 0]),
        zoom: options.zoom || 2,
        minZoom: options.minZoom || 2,
        maxZoom: options.maxZoom || 19,
      }),
    });

    // LayerSwitcher: the ol-ext control has been superseded by the custom
    // LayerPicker (Design B — Bottom Sheet Grid, see
    // BisaWofa-LayerSwitcher-Designs.pdf v1.1). Disabling the ol-ext
    // control here so the two don't visually compete; the LayerPicker is
    // instantiated by main.js and triggered from the "Layers" dock button.
    // const layerSwitcher = new LayerSwitcher({ collapsed: true, mouseover: true, extent: false, trash: false, oninfo: null });
    // this.map.addControl(layerSwitcher);

    // GPS: live-position + trail rendering layers, and the expandable
    // "My Location" control (Locate me + Record trail). This replaces the
    // old ol-ext GeolocationButton — the locate-me action now lives inside
    // the expandable control, and trail recording is wired by main.js to the
    // reusable GeoTracker engine (src/geotracker/).
    this._initGpsRendering();
    this._createLocationControl();

    // Add SearchNominatim control
    const searchNominatim = new SearchNominatim({
      placeholder: t('map.searchPlaceholder'),
      typing: 300,
      minLength: 3,
      maxItems: 10,
      collapsed: true,
    });
    this.map.addControl(searchNominatim);

    // Handle search result selection
    searchNominatim.on('select', (event) => {
      const searchResult = event.search;
      if (searchResult) {
        const lon = parseFloat(searchResult.lon);
        const lat = parseFloat(searchResult.lat);
        const lonLat = [lon, lat];
        const coordinate = fromLonLat(lonLat);

        this.navigateTo(lon, lat, 14);

        const result = {
          coordinate: coordinate,
          lonLat: lonLat,
          name: searchResult.display_name || searchResult.name || 'Unknown',
          searchResult: searchResult,
        };
        this.searchSelectCallbacks.forEach(cb => cb(result));
      }
    });

    this.searchNominatim = searchNominatim;
    this.searchSelectCallbacks = [];

    // ── Route Layer (OSRM directions) ──────────────────────────────
    this.routeSource = new VectorSource();
    this.routeLayer = new VectorLayer({
      title: 'Route',
      source: this.routeSource,
      style: [
        // Translucent outer stroke
        new Style({
          stroke: new Stroke({ color: 'rgba(139, 0, 139, 0.25)', width: 10 }),
        }),
        // Inner dashed line
        new Style({
          stroke: new Stroke({ color: '#8B008B', width: 4, lineDash: [10, 6] }),
        }),
      ],
    });
    this.overlayGroup.getLayers().insertAt(0, this.routeLayer);

    // Track selected feature
    this.selectedFeature = null;

    // When true, hover popups are suppressed (e.g. during measurement drawing)
    this.hoverPopupDisabled = false;

    // Create popup overlay for hover
    this.createPopup();

    // Create Add Location popup form
    this.createAddLocationPopup();

    // Create interactive POI action popup (with "Navigate to")
    this.createPoiActionPopup();
    this.navigateToCallbacks = [];

    // Create circle POI count popup
    this.createCirclePoiPopup();
  }

  /**
   * Update i18n strings in map controls and popup.
   * Called when the language changes.
   */
  updateI18n() {
    // Update search placeholder
    const searchInput = this.searchNominatim?.element?.querySelector('input');
    if (searchInput) {
      searchInput.placeholder = t('map.searchPlaceholder');
    }

    // The Add-Location form is a glass modal built fresh on each open
    // (showAddLocationPopup), so it always reflects the current language —
    // no static DOM to update here.
  }

  /**
   * Create the popup overlay element and add to map
   */
  createPopup() {
    this.popupElement = document.createElement('div');
    this.popupElement.className = 'map-popup';
    this.popupElement.style.cssText = `
      position: absolute;
      background: white;
      border-radius: 8px;
      padding: 10px 14px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.25);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 13px;
      min-width: 150px;
      max-width: 280px;
      pointer-events: none;
      z-index: 1000;
      border: 1px solid #e0e0e0;
    `;

    this.popup = new Overlay({
      element: this.popupElement,
      positioning: 'bottom-center',
      offset: [0, -15],
      stopEvent: false,
    });

    this.map.addOverlay(this.popup);
    this.setupHoverPopup();
  }

  /**
   * Set up the hover popup behavior
   */
  setupHoverPopup() {
    let currentFeature = null;

    this.map.on('pointermove', (evt) => {
      if (evt.dragging) {
        this.hidePopup();
        return;
      }

      // Suppress hover popups when drawing/measuring
      if (this.hoverPopupDisabled) {
        if (currentFeature) {
          currentFeature = null;
          this.hidePopup();
        }
        this.map.getTargetElement().style.cursor = '';
        return;
      }

      const feature = this.map.forEachFeatureAtPixel(evt.pixel, (f) => f);

      if (feature && feature !== currentFeature) {
        currentFeature = feature;
        this.showPopup(feature, evt.coordinate);
      } else if (!feature && currentFeature) {
        currentFeature = null;
        this.hidePopup();
      }

      this.map.getTargetElement().style.cursor = feature ? 'pointer' : '';
    });

    this.map.getTargetElement().addEventListener('mouseleave', () => {
      this.hidePopup();
      currentFeature = null;
    });
  }

  /**
   * Show popup with feature attributes (i18n-aware).
   * Handles user markers, individual POIs, and POI clusters.
   */
  showPopup(feature, coordinate) {
    // Cluster feature (from clusterSource)
    const clusterMembers = feature.get('features');
    if (clusterMembers) {
      if (clusterMembers.length === 1) {
        this.showPoiPopup(clusterMembers[0], coordinate);
      } else {
        this.showClusterPopup(clusterMembers, coordinate);
      }
      return;
    }

    // Direct POI feature (defensive fallback)
    if (feature.get('featureType') === 'poi') {
      this.showPoiPopup(feature, coordinate);
      return;
    }

    // User-added marker
    this.showMarkerPopup(feature, coordinate);
  }

  /**
   * Hover popup for a multi-feature POI cluster.
   * Shows count per amenity type.
   */
  showClusterPopup(members, coordinate) {
    const counts = {};
    members.forEach(f => {
      const a = f.get('amenity') || 'unknown';
      counts[a] = (counts[a] || 0) + 1;
    });

    let html = `<div style="font-weight:600;font-size:14px;margin-bottom:6px;color:#8B008B;">${members.length} POIs</div>`;
    for (const [amenity, count] of Object.entries(counts)) {
      const emoji = this.getAmenityEmoji(amenity);
      const label = amenity.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      html += `<div style="font-size:12px;margin-bottom:2px;">${emoji} ${this.escapeHtml(label)}: <strong>${count}</strong></div>`;
    }

    this.popupElement.innerHTML = html;
    this.popup.setPosition(coordinate);
  }

  /**
   * Popup for user-added location markers
   */
  showMarkerPopup(feature, coordinate) {
    const name = feature.get('name') || 'Unnamed';
    const category = feature.get('category') || 'default';
    const description = feature.get('description');
    const lon = feature.get('lon');
    const lat = feature.get('lat');
    const emoji = this.getEmoji(category);
    const categoryLabel = getCategoryLabel(category);

    let html = `
      <div style="font-weight: 600; font-size: 14px; margin-bottom: 6px; color: #8B008B;">
        ${emoji} ${this.escapeHtml(name)}
      </div>
    `;

    const categoryColors = {
      'water': '#3b82f6',
      'school': '#f59e0b',
      'health': '#ef4444',
      'market': '#8b5cf6',
      'hotel': '#0891b2',
      'restaurant': '#d97706',
      'default': '#556B2F',
      'other': '#6b7280'
    };
    const catColor = categoryColors[category] || '#6b7280';
    html += `
      <div style="margin-bottom: 6px;">
        <span style="
          background: ${catColor}20;
          color: ${catColor};
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 500;
        ">${this.escapeHtml(categoryLabel)}</span>
      </div>
    `;

    if (description) {
      html += `
        <div style="color: #555; font-size: 12px; margin-bottom: 6px; line-height: 1.4;">
          ${this.escapeHtml(description)}
        </div>
      `;
    }

    if (lon !== undefined && lat !== undefined) {
      html += `
        <div style="color: #888; font-size: 11px; font-family: monospace;">
          ${Number(lon).toFixed(5)}, ${Number(lat).toFixed(5)}
        </div>
      `;
    }

    this.popupElement.innerHTML = html;
    this.popup.setPosition(coordinate);
  }

  /**
   * Popup / tooltip for POI features (from PostGIS/OSM data).
   * Shows amenity, brand, and name.
   */
  showPoiPopup(feature, coordinate) {
    const amenity = feature.get('amenity') || '';
    const brand = feature.get('brand') || '';
    const name = feature.get('name') || '';
    const osmId = feature.get('osm_id');
    const lon = feature.get('lon');
    const lat = feature.get('lat');
    const emoji = this.getAmenityEmoji(amenity);

    // Amenity display label (capitalize and replace underscores)
    const amenityLabel = amenity.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

    // Amenity type colours
    const amenityColors = {
      'fuel': '#e65100',
      'bus_station': '#1565c0',
      'taxi': '#f9a825',
    };
    const amenityColor = amenityColors[amenity] || '#556B2F';

    let html = '';

    // Name header
    const displayName = name || brand || amenityLabel;
    html += `
      <div style="font-weight: 600; font-size: 14px; margin-bottom: 6px; color: #8B008B;">
        ${emoji} ${this.escapeHtml(displayName)}
      </div>
    `;

    // Amenity badge
    html += `
      <div style="margin-bottom: 4px;">
        <span style="
          background: ${amenityColor}20;
          color: ${amenityColor};
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 500;
        ">${this.escapeHtml(amenityLabel)}</span>
      </div>
    `;

    // Brand (if present and different from name)
    if (brand && brand !== name) {
      html += `
        <div style="color: #555; font-size: 12px; margin-bottom: 4px;">
          ${this.escapeHtml(brand)}
        </div>
      `;
    }

    // Coordinates
    if (lon !== undefined && lat !== undefined) {
      html += `
        <div style="color: #888; font-size: 11px; font-family: monospace;">
          ${Number(lon).toFixed(5)}, ${Number(lat).toFixed(5)}
        </div>
      `;
    }

    this.popupElement.innerHTML = html;
    this.popup.setPosition(coordinate);
  }

  hidePopup() {
    this.popup.setPosition(undefined);
  }

  escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Create the Add Location popup form overlay (i18n-aware)
   */
  createAddLocationPopup() {
    // The Add-Location form is now a frosted-glass modal (the BisaWofa form
    // design principle — see src/components/glassForm.js), opened on demand by
    // showAddLocationPopup(). No persistent overlay DOM is created here.
    this.addLocationCoords = null;
    this.addLocationCallbacks = [];
    this._addLocationModalOpen = false;
  }

  /**
   * Open the glass Add-Location form for a tapped map coordinate. On submit,
   * registered onAddLocation callbacks receive { name, category, description,
   * lon, lat }.
   * @param {import('ol/coordinate').Coordinate} coordinate map-projection coord
   */
  async showAddLocationPopup(coordinate) {
    if (this._addLocationModalOpen) return;
    const [lon, lat] = toLonLat(coordinate);
    this.addLocationCoords = { lon, lat };

    const categoryOptions = Object.entries(this.categoryEmojis).map(([key, { emoji }]) => ({
      value: key,
      label: `${emoji} ${getCategoryLabel(key)}`,
    }));

    this._addLocationModalOpen = true;
    let data;
    try {
      data = await openGlassModal({
        title: t('addLocation.title'),
        subtitle: `📍 ${lon.toFixed(6)}, ${lat.toFixed(6)}`,
        icon: 'bi-geo-alt-fill',
        submitLabel: t('addLocation.submit'),
        cancelLabel: t('general.cancel') || 'Cancel',
        fields: [
          {
            name: 'name', id: 'map-location-name', label: t('addLocation.name'),
            type: 'text', required: true, voice: true,
            placeholder: t('addLocation.namePlaceholder'), maxlength: 160,
          },
          {
            name: 'category', id: 'map-location-category', label: t('addLocation.category'),
            type: 'select', options: categoryOptions,
          },
          {
            name: 'description', id: 'map-location-description', label: t('addLocation.description'),
            type: 'textarea', voice: true,
            placeholder: t('addLocation.descriptionPlaceholder'), maxlength: 500,
          },
        ],
      });
    } finally {
      this._addLocationModalOpen = false;
    }

    if (!data || !this.addLocationCoords) { this.addLocationCoords = null; return; }

    const payload = {
      name: data.name,
      category: data.category,
      description: data.description,
      lon: this.addLocationCoords.lon,
      lat: this.addLocationCoords.lat,
    };
    this.addLocationCoords = null;
    this.addLocationCallbacks.forEach((cb) => {
      try { cb(payload); } catch (err) { console.error('[MapView] onAddLocation callback error:', err); }
    });
  }

  /** Dismiss any open Add-Location form. The glass modal self-manages its
   *  lifecycle (backdrop / Escape), so this is a no-op kept for API
   *  compatibility with callers that defensively hide popups. */
  hideAddLocationPopup() {
    this.addLocationCoords = null;
  }

  // ========================================================================
  // POI Action Popup (interactive — "Navigate to")
  // ========================================================================

  /**
   * Create the interactive POI action popup overlay.
   * Shown on POI click with a "Navigate to" button.
   */
  createPoiActionPopup() {
    this.poiActionPopupElement = document.createElement('div');
    this.poiActionPopupElement.className = 'poi-action-popup';

    this.poiActionPopup = new Overlay({
      element: this.poiActionPopupElement,
      positioning: 'bottom-center',
      offset: [0, -15],
      stopEvent: true,
      autoPan: true,
      autoPanAnimation: { duration: 250 },
    });

    this.map.addOverlay(this.poiActionPopup);
  }

  /**
   * Show the interactive POI popup with "Navigate to" button.
   */
  showPoiActionPopup(feature, coordinate) {
    const amenity = feature.get('amenity') || '';
    const brand = feature.get('brand') || '';
    const name = feature.get('name') || '';
    const lon = feature.get('lon');
    const lat = feature.get('lat');
    const emoji = this.getAmenityEmoji(amenity);
    const amenityLabel = amenity.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    const displayName = name || brand || amenityLabel;

    const amenityColors = {
      'fuel': '#e65100',
      'bus_station': '#1565c0',
      'taxi': '#f9a825',
    };
    const amenityColor = amenityColors[amenity] || '#556B2F';

    this.poiActionPopupElement.innerHTML = `
      <div class="poi-action-popup-header">
        <span>${emoji} ${this.escapeHtml(displayName)}</span>
        <button type="button" class="poi-action-popup-close" aria-label="Close">&times;</button>
      </div>
      <div class="poi-action-popup-body">
        <span class="poi-action-badge" style="background:${amenityColor}20;color:${amenityColor};">
          ${this.escapeHtml(amenityLabel)}
        </span>
        ${brand && brand !== name ? `<div class="poi-action-brand">${this.escapeHtml(brand)}</div>` : ''}
        <div class="poi-action-coords">${Number(lon).toFixed(5)}, ${Number(lat).toFixed(5)}</div>
        <button type="button" class="poi-action-navigate-btn" data-lon="${lon}" data-lat="${lat}">
          <i class="bi bi-sign-turn-right"></i> Navigate to
        </button>
      </div>
    `;

    // Close button
    this.poiActionPopupElement.querySelector('.poi-action-popup-close')
      .addEventListener('click', () => this.hidePoiActionPopup());

    // Navigate button
    this.poiActionPopupElement.querySelector('.poi-action-navigate-btn')
      .addEventListener('click', (e) => {
        const destLon = parseFloat(e.currentTarget.dataset.lon);
        const destLat = parseFloat(e.currentTarget.dataset.lat);
        this.navigateToCallbacks.forEach(cb => cb({
          lon: destLon,
          lat: destLat,
          name: displayName,
          amenity,
          emoji,
        }));
        this.hidePoiActionPopup();
      });

    this.poiActionPopup.setPosition(coordinate);
  }

  hidePoiActionPopup() {
    this.poiActionPopup.setPosition(undefined);
  }

  // ── Circle POI Count Popup ────────────────────────────────────────

  createCirclePoiPopup() {
    this.circlePoiPopupElement = document.createElement('div');
    this.circlePoiPopupElement.className = 'circle-poi-popup';

    this.circlePoiPopup = new Overlay({
      element: this.circlePoiPopupElement,
      positioning: 'bottom-center',
      offset: [0, -15],
      stopEvent: true,
      autoPan: true,
      autoPanAnimation: { duration: 250 },
    });

    this.map.addOverlay(this.circlePoiPopup);
  }

  /**
   * Show the circle POI count popup.
   * @param {import('ol/coordinate').Coordinate} coordinate  Map coordinate for positioning
   * @param {Object<string, number>} counts   e.g. { fuel: 5, bus_station: 2, taxi: 0 }
   * @param {number} radius   Circle radius in metres
   */
  showCirclePoiPopup(coordinate, counts, radius) {
    const radiusStr = radius > 1000
      ? (Math.round((radius / 1000) * 100) / 100) + ' km'
      : Math.round(radius) + ' m';

    const items = [
      { key: 'fuel',        emoji: '\u26FD',  label: 'Fuel',        color: '#e65100' },
      { key: 'bus_station', emoji: '\uD83D\uDE8C', label: 'Bus Station', color: '#1565c0' },
      { key: 'taxi',        emoji: '\uD83D\uDE95', label: 'Taxi',        color: '#f9a825' },
    ];

    let rowsHtml = '';
    let total = 0;
    for (const { key, emoji, label, color } of items) {
      const count = counts[key] || 0;
      total += count;
      rowsHtml += `
        <div class="circle-poi-row">
          <span class="circle-poi-emoji">${emoji}</span>
          <span class="circle-poi-label" style="color:${color};">${this.escapeHtml(label)}</span>
          <span class="circle-poi-count">${count}</span>
        </div>`;
    }

    this.circlePoiPopupElement.innerHTML = `
      <div class="circle-poi-popup-header">
        <span>\u2B55 Circle \u00B7 ${this.escapeHtml(radiusStr)}</span>
        <button type="button" class="circle-poi-popup-close" aria-label="Close">&times;</button>
      </div>
      <div class="circle-poi-popup-body">
        ${rowsHtml}
        <div class="circle-poi-total">
          Total: <strong>${total}</strong>
        </div>
      </div>
    `;

    this.circlePoiPopupElement.querySelector('.circle-poi-popup-close')
      .addEventListener('click', () => this.hideCirclePoiPopup());

    this.circlePoiPopup.setPosition(coordinate);
  }

  hideCirclePoiPopup() {
    this.circlePoiPopup.setPosition(undefined);
  }

  /**
   * Register a callback for "Navigate to" button clicks.
   * Callback receives { lon, lat, name, amenity, emoji }.
   */
  onNavigateTo(callback) {
    this.navigateToCallbacks.push(callback);
  }

  // ========================================================================
  // Route Display (OSRM)
  // ========================================================================

  /**
   * Draw a route on the map from an array of [lon, lat] coordinate pairs.
   * @param {Array<[number,number]>} coordinates  OSRM geometry.coordinates
   */
  showRoute(coordinates) {
    this.clearRoute();

    const lineCoords = coordinates.map(c => fromLonLat(c));
    const routeFeature = new Feature({
      geometry: new LineString(lineCoords),
    });
    this.routeSource.addFeature(routeFeature);

    // Fit view to route extent
    const extent = this.routeSource.getExtent();
    if (extent && extent[0] !== Infinity) {
      this.map.getView().fit(extent, {
        padding: [80, 80, 120, 80],
        duration: 500,
        maxZoom: 16,
      });
    }
  }

  /**
   * Clear the route line from the map.
   */
  clearRoute() {
    this.routeSource.clear();
  }

  /**
   * Register a callback for when a location is submitted via the Add-Location
   * glass form. Callbacks receive { name, category, description, lon, lat } and
   * are fired from showAddLocationPopup() on submit.
   */
  onAddLocation(callback) {
    this.addLocationCallbacks.push(callback);
  }

  /**
   * Create base layers group for LayerSwitcher
   */
  createBaseLayers(defaultBasemap) {

    const topoLayer = new TileLayer({
      title: 'Topographic',
      type: 'base',
      visible: defaultBasemap === 'topo',
      source: new XYZ({
        url: 'https://{a-c}.tile.opentopomap.org/{z}/{x}/{y}.png',
        attributions: 'Map data: © OpenTopoMap',
        maxZoom: 17,
        crossOrigin: 'anonymous',
      }),
    });

    const cartoLightLayer = new TileLayer({
      title: 'Carto Light',
      type: 'base',
      visible: defaultBasemap === 'carto-light',
      source: new XYZ({
        url: 'https://{a-c}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
        attributions: '© CARTO',
        maxZoom: 19,
        crossOrigin: 'anonymous',
      }),
    });

    const cartoDarkLayer = new TileLayer({
      title: 'Carto Dark',
      type: 'base',
      visible: defaultBasemap === 'carto-dark',
      source: new XYZ({
        url: 'https://{a-c}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
        attributions: '© CARTO',
        maxZoom: 19,
        crossOrigin: 'anonymous',
      }),
    });

    const osmCycleLayer = new TileLayer({
      title: 'OSM Cycle map',
      type: 'base',
      visible: false,
      source: new OSM({
        "url" : "https://tile.thunderforest.com/cycle/{z}/{x}/{y}.png?apikey=ae1339c46dd3446b9c491e7336d38760"
      }),
    });

    const satelliteLayer = new TileLayer({
      title: 'Satellite',
      type: 'base',
      visible: defaultBasemap === 'satellite',
      source: new XYZ({
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        attributions: 'Tiles © Esri',
        maxZoom: 19,
        crossOrigin: 'anonymous',
      }),
    });

    const googleLayer = new TileLayer({
      title: 'Google Sat',
      type: 'base',
      visible: defaultBasemap === 'googlesat',
      source: new XYZ({
        url: 'http://mt0.google.com/vt/lyrs=y&hl=en&x={x}&y={y}&z={z}&s=Ga',
        attributions: 'Tiles © Google',
        maxZoom: 19,
        crossOrigin: 'anonymous',
      }),
    });

    const osmLayer = new TileLayer({
      title: 'OpenStreetMap',
      type: 'base',
      visible: defaultBasemap === 'osm',
      source: new OSM(),
    });

    return new LayerGroup({
      title: 'Base Maps',
      layers: [
        cartoLightLayer,
        cartoDarkLayer,
        satelliteLayer,
        osmCycleLayer,
        topoLayer,
        googleLayer,
        osmLayer,
      ],
    });
  }

  // ========================================================================
  // POI Layer Methods
  // ========================================================================

  /**
   * Get the emoji for an OSM amenity type
   */
  getAmenityEmoji(amenity) {
    return this.amenityEmojis[amenity] || '📌';
  }

  /**
   * Style function for POI features.
   * Returns null for amenities not in the active filter set (hides them).
   */
  getPoiStyle(feature) {
    const amenity = feature.get('amenity') || '';

    // If a filter is set, hide features whose amenity is not active
    if (this.poiFilterSet && !this.poiFilterSet.has(amenity)) {
      return null;
    }

    const emoji = this.getAmenityEmoji(amenity);

    return new Style({
      text: new Text({
        text: emoji,
        font: '24px sans-serif',
        textBaseline: 'bottom',
        textAlign: 'center',
        offsetY: -2,
      }),
    });
  }

  /**
   * Style function for the clustered POI layer.
   * Single-feature clusters render the original emoji.
   * Multi-feature clusters render a numbered circle badge.
   */
  getClusterStyle(feature) {
    const members = feature.get('features');
    if (!members || members.length === 0) return null;

    // Single feature → original emoji
    if (members.length === 1) {
      return this.getPoiStyle(members[0]);
    }

    // Multi-feature cluster → circle badge (cached by size)
    const size = members.length;
    if (this._clusterStyleCache[size]) {
      return this._clusterStyleCache[size];
    }

    const radius = Math.min(30, Math.max(16, 10 + Math.log2(size) * 4));
    const style = new Style({
      image: new Circle({
        radius,
        fill: new Fill({ color: '#8B008B' }),
        stroke: new Stroke({ color: '#FFE96A', width: 2.5 }),
      }),
      text: new Text({
        text: size.toString(),
        font: `bold ${Math.min(14, 10 + Math.floor(size / 50))}px sans-serif`,
        fill: new Fill({ color: '#FFFFFF' }),
        textAlign: 'center',
        textBaseline: 'middle',
      }),
    });

    this._clusterStyleCache[size] = style;
    return style;
  }

  /**
   * Set which amenity types are visible on the POI layer.
   * @param {Set<string>|null} amenities  Set of active amenity keys, or null for all.
   */
  setPoiFilter(amenities) {
    this.poiFilterSet = amenities;
    this._clusterStyleCache = {};
    // Re-cluster with updated filter (geometryFunction reads poiFilterSet)
    this.clusterSource.refresh();
  }

  /**
   * Count POI features per amenity type.
   * @returns {Object<string,number>}  e.g. { fuel: 2765, taxi: 109 }
   */
  getPoiCounts() {
    const counts = {};
    this.poiSource.getFeatures().forEach(f => {
      const a = f.get('amenity');
      if (a) counts[a] = (counts[a] || 0) + 1;
    });
    return counts;
  }

  /**
   * Find POI features nearest to the given position, sorted by distance.
   * Respects the current poiFilterSet (hidden amenities are excluded).
   *
   * @param {number} lon  Longitude (WGS 84)
   * @param {number} lat  Latitude (WGS 84)
   * @param {Object} [opts]
   * @param {number} [opts.limit=20]        Max results to return
   * @param {Set<string>} [opts.amenities]  Restrict to these amenity types (defaults to current filter)
   * @returns {Array<{lon:number, lat:number, distance:number, name:string, amenity:string, emoji:string, brand:string|null}>}
   */
  findNearestPOI(lon, lat, opts = {}) {
    const limit = opts.limit ?? 20;
    const filter = opts.amenities ?? this.poiFilterSet;
    const origin = [lon, lat];

    const results = [];

    this.poiSource.getFeatures().forEach(f => {
      const amenity = f.get('amenity') || '';

      // Skip hidden amenities
      if (filter && !filter.has(amenity)) return;

      const fLon = f.get('lon');
      const fLat = f.get('lat');
      if (fLon == null || fLat == null) return;

      const distance = getDistance(origin, [fLon, fLat]);

      results.push({
        lon: fLon,
        lat: fLat,
        distance,
        name: f.get('name') || f.get('brand') || '',
        amenity,
        emoji: this.getAmenityEmoji(amenity),
        brand: f.get('brand') || null,
      });
    });

    // Sort by distance ascending, take top N
    results.sort((a, b) => a.distance - b.distance);
    return results.slice(0, limit);
  }

  /**
   * Load POI data from GeoJSON (from the PHP API or a static file).
   * Expects a standard GeoJSON FeatureCollection with properties:
   *   osm_id, amenity, brand, name
   *
   * @param {string} url - URL of the GeoJSON endpoint
   * @returns {Promise<number>} Number of features loaded
   */
  async loadPOI(url = `${import.meta.env.BASE_URL}api/poi.php`) {
    console.log('[MapView] Loading POI from:', url);

    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const geojson = await response.json();
      console.log('[MapView] POI GeoJSON received:', geojson.metadata?.count ?? geojson.features?.length, 'features');

      // Clear existing POI features
      this.poiSource.clear();

      if (!geojson.features || geojson.features.length === 0) {
        console.warn('[MapView] No POI features in response');
        return 0;
      }

      // Parse GeoJSON into OL features
      const format = new GeoJSON();
      const features = format.readFeatures(geojson, {
        featureProjection: 'EPSG:3857', // Map projection
        dataProjection: 'EPSG:4326',    // GeoJSON is WGS84
      });

      // Tag each feature so popup can distinguish POI from user markers
      features.forEach(f => {
        f.set('featureType', 'poi');
        // Store lon/lat for popup display
        const coords = f.getGeometry().getCoordinates();
        const [lon, lat] = toLonLat(coords);
        f.set('lon', lon);
        f.set('lat', lat);
      });

      this.poiSource.addFeatures(features);
      console.log('[MapView] POI loaded:', features.length, 'features added to map');

      return features.length;

    } catch (error) {
      console.error('[MapView] Failed to load POI:', error);
      throw error;
    }
  }

  /**
   * Clear all POI features
   */
  clearPOI() {
    this.poiSource.clear();
  }

  /**
   * Get POI layer for external access
   */
  getPoiLayer() {
    return this.poiLayer;
  }

  /**
   * Get POI source for external access
   */
  getPoiSource() {
    return this.poiSource;
  }

  /**
   * Get style for a feature (handles selection state)
   */
  getFeatureStyle(feature) {
    const category = feature.get('category') || 'default';
    const emoji = this.getEmoji(category);

    if (feature === this.selectedFeature) {
      return [
        new Style({
          image: new Circle({
            radius: 22,
            fill: new Fill({ color: 'rgba(220, 38, 38, 0.25)' }),
            stroke: new Stroke({ color: '#dc2626', width: 3 }),
          }),
        }),
        new Style({
          text: new Text({
            text: emoji,
            font: '40px sans-serif',
            textBaseline: 'bottom',
            textAlign: 'center',
            offsetY: -5,
          }),
        }),
      ];
    }

    const customStyle = feature.get('style');
    if (customStyle) {
      return customStyle;
    }

    if (this.categoryStyles[category]) {
      return this.categoryStyles[category];
    }

    return this.defaultStyle;
  }

  /**
   * Set category-based styles with emojis
   */
  setCategoryStyles(styles) {
    for (const [category, config] of Object.entries(styles)) {
      if (config.emoji) {
        if (!this.categoryEmojis[category]) {
          this.categoryEmojis[category] = { emoji: config.emoji };
        } else {
          this.categoryEmojis[category].emoji = config.emoji;
        }
      }

      const emoji = this.getEmoji(category);
      const fontSize = config.fontSize || 28;
      this.categoryStyles[category] = this.createEmojiStyle(emoji, fontSize);
    }

    this.markerSource.changed();
  }

  addMarker(lon, lat, properties = {}) {
    console.log('[MapView] Adding marker at', lon, lat, 'with properties:', properties);

    const feature = new Feature({
      geometry: new Point(fromLonLat([lon, lat])),
      ...properties,
    });

    feature.set('lon', lon);
    feature.set('lat', lat);

    this.markerSource.addFeature(feature);
    console.log('[MapView] Marker added, total features:', this.markerSource.getFeatures().length);
    return feature;
  }

  addMarkers(locations) {
    console.log('[MapView] Adding', locations.length, 'markers');

    const features = locations.map((loc) => {
      const feature = new Feature({
        geometry: new Point(fromLonLat([loc.longitude, loc.latitude])),
        id: loc.id,
        name: loc.name,
        description: loc.description,
        category: loc.category,
        lon: loc.longitude,
        lat: loc.latitude,
      });
      return feature;
    });

    this.markerSource.addFeatures(features);
    console.log('[MapView] Markers added, total features:', this.markerSource.getFeatures().length);
    return features;
  }

  clearMarkers() {
    this.markerSource.clear();
    this.selectedFeature = null;
  }

  removeMarker(featureOrId) {
    if (typeof featureOrId === 'object') {
      this.markerSource.removeFeature(featureOrId);
    } else {
      const feature = this.markerSource.getFeatures().find(
        f => f.get('id') === featureOrId
      );
      if (feature) {
        this.markerSource.removeFeature(feature);
      }
    }
  }

  getMarkers() {
    return this.markerSource.getFeatures();
  }

  findMarker(id) {
    return this.markerSource.getFeatures().find(f => f.get('id') === id);
  }

  selectMarker(featureOrId) {
    if (typeof featureOrId === 'object') {
      this.selectedFeature = featureOrId;
    } else {
      this.selectedFeature = this.findMarker(featureOrId);
    }
    this.markerSource.changed();
    return this.selectedFeature;
  }

  clearSelection() {
    this.selectedFeature = null;
    this.markerSource.changed();
  }

  zoomTo(lon, lat, zoom = 15) {
    this.map.getView().animate({
      center: fromLonLat([lon, lat]),
      zoom: zoom,
      duration: 500,
    });
  }

  /**
   * Zoom in to expand a POI cluster, fitting the view to its members' extent.
   */
  zoomToCluster(clusterFeature) {
    const members = clusterFeature.get('features');
    if (!members || members.length <= 1) return;

    const extent = members[0].getGeometry().getExtent().slice();
    for (let i = 1; i < members.length; i++) {
      const e = members[i].getGeometry().getExtent();
      if (e[0] < extent[0]) extent[0] = e[0];
      if (e[1] < extent[1]) extent[1] = e[1];
      if (e[2] > extent[2]) extent[2] = e[2];
      if (e[3] > extent[3]) extent[3] = e[3];
    }

    this.map.getView().fit(extent, {
      padding: [80, 80, 80, 80],
      duration: 500,
      maxZoom: 17,
    });
  }

  fitToMarkers(padding = 50) {
    const extent = this.markerSource.getExtent();
    if (extent && extent[0] !== Infinity) {
      this.map.getView().fit(extent, {
        padding: [padding, padding, padding, padding],
        duration: 500,
        maxZoom: 16,
      });
    }
  }

  getCenter() {
    const center = this.map.getView().getCenter();
    return toLonLat(center);
  }

  getZoom() {
    return this.map.getView().getZoom();
  }

  setCenter(lon, lat) {
    this.map.getView().setCenter(fromLonLat([lon, lat]));
  }

  setZoom(zoom) {
    this.map.getView().setZoom(zoom);
  }

  onClick(callback) {
    this.clickCallbacks.push(callback);

    if (this.clickCallbacks.length === 1) {
      this.map.on('click', (evt) => {
        const [lon, lat] = toLonLat(evt.coordinate);

        let clickedFeature = null;
        this.map.forEachFeatureAtPixel(evt.pixel, (feature) => {
          clickedFeature = feature;
          return true;
        });

        for (const cb of this.clickCallbacks) {
          cb(lon, lat, clickedFeature, evt);
        }
      });
    }

    return () => {
      const index = this.clickCallbacks.indexOf(callback);
      if (index > -1) {
        this.clickCallbacks.splice(index, 1);
      }
    };
  }

  onPointerMove(callback) {
    this.map.on('pointermove', (evt) => {
      if (evt.dragging) return;

      const [lon, lat] = toLonLat(evt.coordinate);

      let hoveredFeature = null;
      this.map.forEachFeatureAtPixel(evt.pixel, (feature) => {
        hoveredFeature = feature;
        return true;
      });

      this.map.getTargetElement().style.cursor = hoveredFeature ? 'pointer' : '';
      callback(lon, lat, hoveredFeature, evt);
    });
  }

  enableHoverCursor() {
    // Cursor changes are now handled by setupHoverPopup()
  }

  // ==========================================================================
  // GPS: current-position + trail rendering, and the expandable Location control
  //
  // NOTE: MapView deliberately knows nothing about the GeoTracker engine,
  // SQLocal, or sync. It only (a) renders what it's told and (b) emits UI
  // intents via callbacks. main.js wires those intents to the GeoTracker so the
  // map stays reusable/decoupled.
  // ==========================================================================

  /** Create the vector layers used to draw the live position and the trail. */
  _initGpsRendering() {
    this._gpsPositionSource = new VectorSource();
    this._gpsTrailSource = new VectorSource();
    this._gpsTrailCoords = []; // [ [x,y], ... ] in map projection

    // Trail line (drawn under the position marker)
    this._gpsTrailLayer = new VectorLayer({
      source: this._gpsTrailSource,
      zIndex: 940,
      style: new Style({
        stroke: new Stroke({ color: '#ff6d00', width: 4, lineCap: 'round', lineJoin: 'round' }),
      }),
      properties: { title: 'GPS Trail', displayInLayerSwitcher: false },
    });

    // Current position: accuracy halo + solid dot
    this._gpsPositionLayer = new VectorLayer({
      source: this._gpsPositionSource,
      zIndex: 950,
      style: (feature) => {
        if (feature.get('_kind') === 'accuracy') {
          return new Style({
            fill: new Fill({ color: 'rgba(139,0,139,0.12)' }),
            stroke: new Stroke({ color: 'rgba(139,0,139,0.35)', width: 1 }),
          });
        }
        return new Style({
          image: new Circle({
            radius: 7,
            fill: new Fill({ color: '#8B008B' }),
            stroke: new Stroke({ color: '#ffffff', width: 2.5 }),
          }),
        });
      },
      properties: { title: 'GPS Position', displayInLayerSwitcher: false },
    });

    this.map.addLayer(this._gpsTrailLayer);
    this.map.addLayer(this._gpsPositionLayer);

    this._gpsCallbacks = { locate: [], record: [] };
    this._gpsRecording = false;
  }

  /** Register a callback fired when the user taps "Locate Me". */
  onLocateMe(cb) { this._gpsCallbacks.locate.push(cb); }
  /** Register a callback fired when the user toggles trail recording. Receives the desired state (true=start). */
  onToggleRecording(cb) { this._gpsCallbacks.record.push(cb); }

  /** Programmatically trigger the "Locate me" action (e.g. from an empty-state CTA). */
  triggerLocateMe() {
    for (const cb of this._gpsCallbacks?.locate || []) {
      try { cb(); } catch (err) { console.error(err); }
    }
  }

  /**
   * Draw / move the current-position marker and accuracy halo.
   * @param {number} lon
   * @param {number} lat
   * @param {number|null} [accuracy] horizontal accuracy in metres
   */
  showCurrentPosition(lon, lat, accuracy = null) {
    if (lon == null || lat == null) return;
    const center = fromLonLat([lon, lat]);
    this._gpsPositionSource.clear();

    if (accuracy && accuracy > 0) {
      // Approximate the accuracy circle in projected units. Good enough for a
      // visual halo at typical zoom levels.
      const resAtLat = accuracy / Math.cos((lat * Math.PI) / 180);
      const halo = new Feature({ geometry: new PolygonGeom([this._circleRing(center, resAtLat)]) });
      halo.set('_kind', 'accuracy');
      this._gpsPositionSource.addFeature(halo);
    }
    const dot = new Feature({ geometry: new Point(center) });
    dot.set('_kind', 'dot');
    this._gpsPositionSource.addFeature(dot);
  }

  /** @private build a ring of coordinates approximating a circle (metres → projected). */
  _circleRing(center, radiusMeters, segments = 48) {
    const ring = [];
    const r = radiusMeters; // EPSG:3857 units are metres (approx, locally)
    for (let i = 0; i <= segments; i++) {
      const a = (i / segments) * 2 * Math.PI;
      ring.push([center[0] + r * Math.cos(a), center[1] + r * Math.sin(a)]);
    }
    return ring;
  }

  /** Smoothly center the view on a coordinate. */
  centerOn(lon, lat, zoom = 16) {
    const view = this.map.getView();
    view.animate({ center: fromLonLat([lon, lat]), zoom, duration: 500 });
  }

  /** Reset the trail line (call when a new recording starts). */
  startTrailRender() {
    this._gpsTrailCoords = [];
    this._gpsTrailSource.clear();
  }

  /** Append a coordinate to the growing trail line. */
  appendTrailPoint(lon, lat) {
    if (lon == null || lat == null) return;
    this._gpsTrailCoords.push(fromLonLat([lon, lat]));
    this._gpsTrailSource.clear();
    if (this._gpsTrailCoords.length >= 2) {
      this._gpsTrailSource.addFeature(new Feature({ geometry: new LineString(this._gpsTrailCoords) }));
    }
  }

  /** Remove the rendered trail (does not affect stored data). */
  clearTrailRender() {
    this._gpsTrailCoords = [];
    this._gpsTrailSource.clear();
  }

  /** Reflect recording state on the control button. */
  setRecordingState(active) {
    this._gpsRecording = !!active;
    if (this._recordBtn) {
      this._recordBtn.classList.toggle('recording', this._gpsRecording);
      this._recordBtn.title = this._gpsRecording ? 'Stop trail recording' : 'Record GPS trail';
      this._recordBtn.innerHTML = this._gpsRecording
        ? '<i class="bi bi-stop-fill"></i>'
        : '<i class="bi bi-record-circle"></i>';
    }
    if (this._locateToggle) this._locateToggle.classList.toggle('recording', this._gpsRecording);
  }

  /**
   * Build the expandable "My Location" control: a main button that reveals two
   * sub-buttons (Locate Me, Record Trail). Anchored bottom-right where the old
   * ol-ext GeolocationButton sat.
   */
  _createLocationControl() {
    const target = this.map.getTargetElement();
    if (!target) return;

    // Main toggle
    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'ls-locate-toggle';
    toggle.title = 'My Location';
    toggle.setAttribute('aria-label', 'My Location');
    toggle.innerHTML = '<i class="bi bi-geo-alt-fill"></i>';
    target.appendChild(toggle);

    // Sub-button cluster (hidden until the main button is tapped)
    const actions = document.createElement('div');
    actions.className = 'ls-locate-actions';
    actions.innerHTML =
      '<button type="button" class="ls-locate-btn ls-locate-me" title="Locate me">' +
        '<i class="bi bi-crosshair"></i></button>' +
      '<button type="button" class="ls-locate-btn ls-locate-record" title="Record GPS trail">' +
        '<i class="bi bi-record-circle"></i></button>';
    target.appendChild(actions);

    this._locateToggle = toggle;
    this._locateActions = actions;
    this._locateMeBtn = actions.querySelector('.ls-locate-me');
    this._recordBtn = actions.querySelector('.ls-locate-record');

    const close = () => { actions.classList.remove('open'); toggle.classList.remove('active'); };
    const open = () => { actions.classList.add('open'); toggle.classList.add('active'); };

    toggle.addEventListener('click', (e) => {
      e.stopPropagation();
      actions.classList.contains('open') ? close() : open();
    });

    // Tap outside closes the cluster (but never while recording, so the stop
    // button stays reachable).
    document.addEventListener('click', (e) => {
      if (!actions.classList.contains('open')) return;
      if (actions.contains(e.target) || toggle.contains(e.target)) return;
      if (this._gpsRecording) return;
      close();
    });

    this._locateMeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      for (const cb of this._gpsCallbacks.locate) { try { cb(); } catch (err) { console.error(err); } }
      if (!this._gpsRecording) close();
    });

    this._recordBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const next = !this._gpsRecording;
      for (const cb of this._gpsCallbacks.record) { try { cb(next); } catch (err) { console.error(err); } }
    });
  }

  getMap() {
    return this.map;
  }

  getMarkerSource() {
    return this.markerSource;
  }

  getMarkersLayer() {
    return this.markersLayer;
  }

  updateSize() {
    this.map.updateSize();
  }

  onSearchSelect(callback) {
    this.searchSelectCallbacks.push(callback);
  }

  navigateTo(lon, lat, zoom = 14, duration = 500) {
    const coordinate = fromLonLat([lon, lat]);
    this.map.getView().animate({
      center: coordinate,
      zoom: zoom,
      duration: duration,
    });
  }
}

// Export OpenLayers utilities for convenience
export { fromLonLat, toLonLat };

export default MapView;
